import type { CompiledRegion, TourStop } from "../../shared/codemap";
import type { GuideLayer, ReviewAnnotation, ReviewGuide } from "../../shared/guide";
import type { FixtureDescriptor, FixtureFile, FixtureFiles } from "./fixtures";
import {
  fileChatModes,
  isFileChatMode,
  parseFileChatEvent,
  type FileChatActivity,
  type FileChatMode,
  type FileChatSource,
} from "./file-chat-protocol";

export { fileChatModes, type FileChatMode };
import type { HostedPreview, HostedReviewContext, HostedReviewThread } from "./hosted";
import { buildUnifiedDiff, diffStats, type UnifiedDiffRow } from "./unified-diff";

export const fileChatAssistants = ["codex", "claude", "copilot"] as const;
export type FileChatAssistant = (typeof fileChatAssistants)[number];

export interface FileChatAssistantMeta {
  id: FileChatAssistant;
  label: string;
}

export const fileChatAssistantMeta: Record<FileChatAssistant, FileChatAssistantMeta> = {
  codex: { id: "codex", label: "Codex" },
  claude: { id: "claude", label: "Claude" },
  copilot: { id: "copilot", label: "Copilot" },
};

export const defaultFileChatAssistant: FileChatAssistant = "codex";
const FILE_CHAT_ASSISTANT_STORAGE_KEY = "chew:file-chat-assistant";
const FILE_CHAT_MODELS_STORAGE_KEY = "chew:file-chat-models";
const FILE_CHAT_SIZE_STORAGE_KEY = "chew:file-chat-size";

export function storedFileChatAssistant(): FileChatAssistant {
  const stored = localStorage.getItem(FILE_CHAT_ASSISTANT_STORAGE_KEY);
  if (stored === "code") {
    persistFileChatAssistant("codex");
    return "codex";
  }
  return fileChatAssistants.includes(stored as FileChatAssistant)
    ? (stored as FileChatAssistant)
    : defaultFileChatAssistant;
}

export function persistFileChatAssistant(assistant: FileChatAssistant): void {
  localStorage.setItem(FILE_CHAT_ASSISTANT_STORAGE_KEY, assistant);
}

const FILE_CHAT_MODE_STORAGE_KEY = "chew:file-chat-mode";

/**
 * Workspace by default. CHEW reviews your own repositories from your own
 * machine, and the checkout it hands over is a disposable worktree, so the
 * useful default is an assistant that can build and test what it is reading.
 */
export const defaultFileChatMode: FileChatMode = "workspace";

export function storedFileChatMode(): FileChatMode {
  const stored = localStorage.getItem(FILE_CHAT_MODE_STORAGE_KEY);
  return isFileChatMode(stored) ? stored : defaultFileChatMode;
}

export function persistFileChatMode(mode: FileChatMode): void {
  localStorage.setItem(FILE_CHAT_MODE_STORAGE_KEY, mode);
}

export const fileChatModeMeta: Record<FileChatMode, { label: string; hint: string }> = {
  "read-only": {
    label: "Read only",
    hint: "Reads the checked-out commit, and cannot change anything or run commands.",
  },
  workspace: {
    label: "Workspace",
    hint: "Edits files and runs commands in a disposable checkout that is never your own working tree.",
  },
};

export interface FileChatMessage {
  role: "user" | "assistant";
  content: string;
  assistant?: FileChatAssistant;
  model?: string;
}

export type FileChatModels = Partial<Record<FileChatAssistant, string>>;

export function storedFileChatModels(): FileChatModels {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(FILE_CHAT_MODELS_STORAGE_KEY) ?? "{}");
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(fileChatAssistants.flatMap((assistant) => {
      const value = (parsed as Record<string, unknown>)[assistant];
      return typeof value === "string" && value.length <= 128 ? [[assistant, value]] : [];
    }));
  } catch {
    return {};
  }
}

export function persistFileChatModels(models: FileChatModels): void {
  localStorage.setItem(FILE_CHAT_MODELS_STORAGE_KEY, JSON.stringify(models));
}

export interface FileChatSize {
  width: number;
  height: number;
}

/** Wide enough to read a diff excerpt and the assistant's answer side by side. */
const DEFAULT_FILE_CHAT_SIZE: FileChatSize = { width: 760, height: 780 };

export type FileChatResizeDirection = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

export interface FileChatOffset {
  x: number;
  y: number;
}

/** How much of a dragged window must stay on screen to remain grabbable. */
const MIN_VISIBLE_FILE_CHAT = 96;

/**
 * Move the popup by `delta` from where its anchor would place it. `frame` is
 * that unmoved rectangle, so the clamp keeps a grabbable strip on screen
 * however far the reviewer drags.
 */
export function moveFileChat(
  offset: FileChatOffset,
  delta: FileChatOffset,
  frame: { left: number; top: number; width: number; height: number },
  viewport = { width: window.innerWidth, height: window.innerHeight },
): FileChatOffset {
  const horizontal = {
    minimum: MIN_VISIBLE_FILE_CHAT - frame.left - frame.width,
    maximum: viewport.width - MIN_VISIBLE_FILE_CHAT - frame.left,
  };
  const vertical = {
    minimum: -frame.top,
    maximum: viewport.height - MIN_VISIBLE_FILE_CHAT - frame.top,
  };
  return {
    x: Math.min(horizontal.maximum, Math.max(horizontal.minimum, offset.x + delta.x)),
    y: Math.min(vertical.maximum, Math.max(vertical.minimum, offset.y + delta.y)),
  };
}

export function clampFileChatSize(
  size: Partial<FileChatSize> | undefined,
  viewport = { width: window.innerWidth, height: window.innerHeight },
): FileChatSize {
  const maxWidth = Math.max(300, viewport.width - 24);
  const maxHeight = Math.max(320, viewport.height - 80);
  const width = Number.isFinite(size?.width) ? Number(size?.width) : DEFAULT_FILE_CHAT_SIZE.width;
  const height = Number.isFinite(size?.height) ? Number(size?.height) : DEFAULT_FILE_CHAT_SIZE.height;
  return {
    width: Math.min(maxWidth, Math.max(300, width)),
    height: Math.min(maxHeight, Math.max(320, height)),
  };
}

export function resizeFileChat(
  size: FileChatSize,
  direction: FileChatResizeDirection,
  delta: { x: number; y: number },
  viewport = { width: window.innerWidth, height: window.innerHeight },
): FileChatSize {
  return clampFileChatSize({
    width: size.width + (direction.includes("e") ? delta.x : direction.includes("w") ? -delta.x : 0),
    height: size.height + (direction.includes("s") ? delta.y : direction.includes("n") ? -delta.y : 0),
  }, viewport);
}

export function storedFileChatSize(): FileChatSize {
  try {
    return clampFileChatSize(JSON.parse(localStorage.getItem(FILE_CHAT_SIZE_STORAGE_KEY) ?? "null") as Partial<FileChatSize>);
  } catch {
    return clampFileChatSize(undefined);
  }
}

export function persistFileChatSize(size: FileChatSize): void {
  localStorage.setItem(FILE_CHAT_SIZE_STORAGE_KEY, JSON.stringify(size));
}

export interface FileChatCapability {
  id: FileChatAssistant;
  label: string;
  installed: boolean;
  version?: string;
  defaultModel: { id: ""; label: string };
  models: Array<{ id: string; label: string }>;
  supportsCustomModel: boolean;
  discovery: {
    provenance: string;
    complete: boolean;
    error?: string;
  };
}

export interface FileChatContextInput {
  path: string;
  descriptor: FixtureDescriptor;
  file?: FixtureFile;
  files: FixtureFiles;
  pullDetails?: HostedPreview["pull"];
  pullContext?: Pick<HostedReviewContext, "conversation"> &
    Partial<Pick<HostedReviewContext, "resolution" | "ci" | "changedSinceReview">>;
  reviewThreads: HostedReviewThread[];
  reviewGuidance?: {
    overview: Pick<ReviewGuide["overview"], "problem" | "approach" | "mainRisk" | "reviewFocus">;
    step: Pick<GuideLayer, "title" | "narrative" | "scrutiny" | "riskTags">;
  };
  fileSemantics?: {
    focus?: Pick<TourStop, "side" | "startLine" | "endLine" | "nature" | "concerns" | "scrutiny" | "coverageMode">;
    regions: Array<Pick<CompiledRegion, "nature" | "concerns" | "scrutiny" | "verification" | "changedLoc">>;
    annotations: Array<Pick<ReviewAnnotation, "side" | "startLine" | "endLine" | "tone" | "body">>;
  };
}

function organizationOf(repository: string): string {
  return repository.split("/")[0] ?? repository;
}

/** Bound the diff we send so a huge file cannot blow the request budget. */
const MAX_DIFF_ROWS = 1_200;
const FOCUS_CONTEXT_ROWS = 80;
const CHANGED_CONTEXT_ROWS = 3;
const MAX_GUIDANCE_CHARS = 8_000;
const MAX_SEMANTICS_CHARS = 12_000;

function focusRowIndices(
  rows: UnifiedDiffRow[],
  focus: NonNullable<NonNullable<FileChatContextInput["fileSemantics"]>["focus"]>,
): number[] {
  return rows.flatMap((row, index) => {
    const line = focus.side === "LEFT" ? row.oldLine : row.newLine;
    return line !== undefined && line >= focus.startLine && line <= focus.endLine ? [index] : [];
  });
}

function focusedDiffRows(
  rows: UnifiedDiffRow[],
  focus: NonNullable<NonNullable<FileChatContextInput["fileSemantics"]>["focus"]>,
): { rows: Array<UnifiedDiffRow | undefined>; focusPreserved: boolean } {
  const focused = focusRowIndices(rows, focus);
  if (focused.length === 0) {
    return { rows: rows.slice(0, MAX_DIFF_ROWS), focusPreserved: false };
  }

  const retained = new Set<number>();
  const addRange = (start: number, end: number) => {
    for (let index = Math.max(0, start); index <= Math.min(rows.length - 1, end); index += 1) {
      if (retained.size >= MAX_DIFF_ROWS) return;
      retained.add(index);
    }
  };
  addRange(focused[0]! - FOCUS_CONTEXT_ROWS, focused.at(-1)! + FOCUS_CONTEXT_ROWS);
  for (let index = 0; index < rows.length && retained.size < MAX_DIFF_ROWS; index += 1) {
    if (rows[index]!.kind === "add" || rows[index]!.kind === "delete") {
      addRange(index - CHANGED_CONTEXT_ROWS, index + CHANGED_CONTEXT_ROWS);
    }
  }

  const selected = [...retained].sort((left, right) => left - right);
  return {
    rows: selected.flatMap((index, position) => {
      const previous = selected[position - 1];
      return previous !== undefined && index > previous + 1
        ? [undefined, rows[index]!]
        : [rows[index]!];
    }),
    focusPreserved: true,
  };
}

function fileDiffText(
  file: FixtureFile | undefined,
  rows: UnifiedDiffRow[],
  focus?: NonNullable<FileChatContextInput["fileSemantics"]>["focus"],
): string {
  if (!file) return "(source unavailable in this review)";
  if (file.binary) return "(binary file, no textual diff)";
  const truncated = rows.length > MAX_DIFF_ROWS;
  const focused = truncated && focus ? focusedDiffRows(rows, focus) : undefined;
  const shown: Array<UnifiedDiffRow | undefined> = focused?.rows ?? (truncated ? rows.slice(0, MAX_DIFF_ROWS) : rows);
  const body = shown
    .map((row) => {
      if (!row) return "      … unchanged or lower-priority diff rows omitted …";
      const marker = row.kind === "add" ? "+" : row.kind === "delete" ? "-" : " ";
      const lineNumber = row.newLine ?? row.oldLine ?? "";
      return `${marker}${String(lineNumber).padStart(5, " ")} ${row.text}`;
    })
    .join("\n");
  return truncated
    ? `${body}\n… (diff truncated at ${MAX_DIFF_ROWS} source rows${focused?.focusPreserved ? "; active focus preserved" : ""})`
    : body;
}

function changedFileList(input: FileChatContextInput): string {
  const paths = Object.keys(input.files.files).sort();
  if (paths.length === 0) return "(no changed files projected)";
  return paths
    .map((path) => {
      const file = input.files.files[path];
      if (!file || file.binary) return `- ${path} (binary)`;
      const stats = diffStats(buildUnifiedDiff(file.base, file.head));
      return `- ${path} (+${stats.additions} -${stats.deletions})`;
    })
    .join("\n");
}

function peopleSection(input: FileChatContextInput): string {
  const pull = input.pullDetails;
  const reviewers = [
    ...(pull?.requestedReviewers ?? []),
    ...(pull?.requestedTeams ?? []),
  ];
  const approvers = input.pullContext?.resolution?.review.approvers ?? [];
  const lines = [
    `Author: ${pull ? `@${pull.author}` : "unknown"}`,
    `Requested reviewers: ${reviewers.length ? reviewers.map((name) => `@${name}`).join(", ") : "none"}`,
    `Approvers: ${approvers.length ? approvers.map((name) => `@${name}`).join(", ") : "none"}`,
  ];
  if (pull?.assignees?.length) lines.push(`Assignees: ${pull.assignees.map((name) => `@${name}`).join(", ")}`);
  if (pull?.labels?.length) lines.push(`Labels: ${pull.labels.join(", ")}`);
  return lines.join("\n");
}

function sourceSection(input: FileChatContextInput): string {
  const pull = input.pullDetails;
  const context = input.pullContext;
  const lines = [
    `Pinned diff base: ${input.files.source.baseSha}`,
    `Pinned diff head: ${input.files.source.headSha}`,
  ];
  if (pull) {
    lines.push(
      `Current pull refs: ${pull.baseRef} @ ${pull.baseSha} ← ${pull.headRef} @ ${pull.headSha}`,
      `Pull state: ${pull.state}${pull.draft ? " (draft)" : ""}`,
    );
  }
  if (context?.resolution) {
    const review = context.resolution.review;
    const merge = context.resolution.merge;
    lines.push(
      `Review decision: ${review.state}; ${review.approvals}/${review.requiredApprovals ?? "unknown"} required approvals`,
      `Changes requested by: ${review.changesRequestedBy.length ? review.changesRequestedBy.map((name) => `@${name}`).join(", ") : "none"}`,
      `Merge state: ${merge.state}; mergeable ${merge.mergeable ?? "unknown"}; unresolved conversations ${merge.conversationResolutionRequired ? "block merging" : "do not block merging"}`,
    );
  }
  return lines.join("\n");
}

function reviewSignalsSection(input: FileChatContextInput): string {
  const context = input.pullContext;
  if (!context) return "(review signals unavailable)";
  const failed = context.ci?.checks.filter((check) => check.state === "failure").map((check) => check.name) ?? [];
  const pending = context.ci?.checks.filter((check) => check.state === "pending").map((check) => check.name) ?? [];
  const conversation = context.conversation;
  const changed = context.changedSinceReview;
  const lines = [
    `CI: ${context.ci?.state ?? "unavailable"}${failed.length ? `; failed: ${failed.join(", ")}` : ""}${pending.length ? `; pending: ${pending.join(", ")}` : ""}`,
    `Review conversation: ${conversation.unresolvedThreadCount} unresolved, ${conversation.outdatedThreadCount} outdated threads`,
  ];
  if (changed) {
    const commitLabel = changed.commits === 1 ? "commit" : "commits";
    lines.push(`Since last review: ${changed.status}; ${changed.commits} ${commitLabel}, +${changed.additions} -${changed.deletions}`);
    if (changed.files.length) {
      lines.push(`Files changed since review: ${changed.files.map((file) => file.path).join(", ")}${changed.truncated ? " (truncated)" : ""}`);
    }
  }
  return lines.join("\n");
}

function boundedSection(value: string, maximum: number, label: string): string {
  if (value.length <= maximum) return value;
  const marker = `\n[… ${label} shortened to fit CHEW's chat context budget]`;
  return `${value.slice(0, maximum - marker.length)}${marker}`;
}

function guidanceSection(input: FileChatContextInput): string {
  const guidance = input.reviewGuidance;
  if (!guidance) return "(no CHEW guidance available)";
  const lines = [
    `Problem: ${guidance.overview.problem ?? "(not specified)"}`,
    `Approach: ${guidance.overview.approach ?? "(not specified)"}`,
    `Main risk: ${guidance.overview.mainRisk}`,
    `Review focus: ${guidance.overview.reviewFocus.length ? guidance.overview.reviewFocus.join("; ") : "(not specified)"}`,
    `Current step: ${guidance.step.title} (${guidance.step.scrutiny} scrutiny)`,
    `Step narrative: ${guidance.step.narrative}`,
    `Step risks: ${guidance.step.riskTags.length ? guidance.step.riskTags.join(", ") : "none identified"}`,
  ];
  return boundedSection(lines.join("\n"), MAX_GUIDANCE_CHARS, "CHEW review guidance");
}

function lineRange(side: "LEFT" | "RIGHT", startLine: number, endLine: number): string {
  const range = startLine === endLine ? `L${startLine}` : `L${startLine}-L${endLine}`;
  return `${side} ${range}`;
}

function semanticsSection(input: FileChatContextInput): string {
  const semantics = input.fileSemantics;
  if (!semantics) return "(no semantic classification available)";
  const lines: string[] = [];
  if (semantics.focus) {
    const focus = semantics.focus;
    lines.push(
      `Active focus: ${lineRange(focus.side, focus.startLine, focus.endLine)}; ${focus.nature}; ${focus.scrutiny} scrutiny; ${focus.coverageMode} coverage`,
      `Active concerns: ${focus.concerns.length ? focus.concerns.join(", ") : "none identified"}`,
    );
  } else {
    lines.push("Active focus: none");
  }
  if (semantics.regions.length) {
    lines.push("File regions:");
    for (const region of semantics.regions) {
      lines.push(
        `- ${region.nature}; ${region.scrutiny}; ${region.verification}; ${region.changedLoc ?? "unknown"} changed lines; concerns: ${region.concerns.join(", ") || "none"}`,
      );
    }
  }
  const annotations = semantics.focus
    ? semantics.annotations.filter((annotation) =>
        annotation.side === semantics.focus!.side &&
        annotation.endLine >= semantics.focus!.startLine &&
        annotation.startLine <= semantics.focus!.endLine
      )
    : semantics.annotations.slice(0, 8);
  if (annotations.length) {
    lines.push("Applicable CHEW annotations:");
    for (const annotation of annotations) {
      lines.push(`- ${lineRange(annotation.side, annotation.startLine, annotation.endLine)} [${annotation.tone}]: ${annotation.body}`);
    }
  }
  return boundedSection(lines.join("\n"), MAX_SEMANTICS_CHARS, "focused-file semantics");
}

export const MAX_FILE_CHAT_COMMENT_CHARS = 24_000;

function commentsSection(input: FileChatContextInput): string {
  const conversation = input.pullContext?.conversation;
  const fileThreads = input.reviewThreads.filter((thread) => thread.path === input.path);
  const orderedThreads = [
    ...fileThreads.filter((thread) => !thread.resolved && !thread.outdated),
    ...fileThreads.filter((thread) => thread.resolved || thread.outdated),
  ];
  const usedUrls = new Set(orderedThreads.flatMap((thread) => thread.comments.map((comment) => comment.url)));
  const items = [
    ...orderedThreads.map((thread) => {
      const status = thread.resolved ? "resolved" : thread.outdated ? "outdated" : "open";
      const location = thread.line ? `L${thread.line}` : "file";
      return `Thread ${location} (${status}):\n${thread.comments
        .map((comment) => `  @${comment.author}: ${comment.body.trim()}`)
        .join("\n")}`;
    }),
    ...(conversation?.recent ?? [])
      .filter((entry) => !usedUrls.has(entry.url))
      .map((entry) => `PR conversation — @${entry.author} (${entry.kind}):\n${entry.body.trim()}`),
  ];

  const retained: string[] = [];
  let used = 0;
  let shortenedBodies = 0;
  let omittedItems = 0;
  for (const item of items) {
    const separator = retained.length ? 2 : 0;
    const available = MAX_FILE_CHAT_COMMENT_CHARS - used - separator;
    if (available <= 0) {
      omittedItems += 1;
      continue;
    }
    if (item.length <= available) {
      retained.push(item);
      used += item.length + separator;
      continue;
    }
    const marker = "\n[… comment shortened to fit CHEW's chat context budget]";
    if (available > marker.length + 80) {
      retained.push(`${item.slice(0, available - marker.length)}${marker}`);
      used = MAX_FILE_CHAT_COMMENT_CHARS;
      shortenedBodies += 1;
    } else {
      omittedItems += 1;
    }
  }

  const completeness = conversation?.completeness;
  const disclosures: string[] = [];
  if (shortenedBodies) disclosures.push(`${shortenedBodies} comment body was shortened by the chat context budget.`);
  if (omittedItems) disclosures.push(`${omittedItems} comment or thread was omitted by the chat context budget.`);
  if (completeness?.issueComments.truncated) {
    disclosures.push(`${completeness.issueComments.total - completeness.issueComments.collected} older PR comments were not collected.`);
  }
  if (completeness?.reviewThreads.truncated || (!completeness && conversation?.truncated)) {
    const omitted = completeness
      ? completeness.reviewThreads.total - completeness.reviewThreads.collected
      : Math.max(0, (conversation?.reviewThreadCount ?? 0) - (conversation?.threads?.length ?? 0));
    disclosures.push(`${omitted} review threads were not collected.`);
  }
  if (completeness?.threadReplies.truncatedThreads) {
    disclosures.push(
      `${completeness.threadReplies.omitted} replies across ${completeness.threadReplies.truncatedThreads} review threads were not collected.`,
    );
  }
  if (completeness?.recent.truncated) {
    disclosures.push(`${completeness.recent.total - completeness.recent.collected} collected conversation entries were omitted from the recent projection.`);
  }
  if (completeness?.bodies?.shortened) {
    disclosures.push(
      `${completeness.bodies.shortened} collected comment bodies were shortened at ${completeness.bodies.limit} characters.`,
    );
  }
  const body = retained.length ? retained.join("\n\n") : "(no current comments)";
  return disclosures.length
    ? `${body}\n\nContext completeness:\n${disclosures.map((item) => `- ${item}`).join("\n")}`
    : body;
}

/**
 * Identify the commit the companion should check out. The pinned diff head is
 * the right target rather than the live pull head: it is the tree the reviewer
 * is looking at, so the checkout and the diff cannot disagree.
 */
export function buildFileChatSource(input: FileChatContextInput): FileChatSource | undefined {
  const headSha = input.files.source.headSha || input.descriptor.headSha;
  if (!headSha) return undefined;
  return {
    repository: input.descriptor.repository,
    headSha,
    ...(input.pullDetails?.headRef ? { headRef: input.pullDetails.headRef } : {}),
    ...(input.descriptor.pullNumber ? { pullNumber: input.descriptor.pullNumber } : {}),
  };
}

/** Assemble the plain-text review context sent alongside the chat messages. */
export function buildFileChatContext(input: FileChatContextInput): string {
  const { descriptor } = input;
  const pull = input.pullDetails;
  const rows = input.file && !input.file.binary ? buildUnifiedDiff(input.file.base, input.file.head) : [];
  const additions = pull?.additions ?? descriptor.stats.additions;
  const deletions = pull?.deletions ?? descriptor.stats.deletions;
  const changedFiles = pull?.changedFiles ?? descriptor.stats.changedFiles;
  const description = (pull?.body ?? descriptor.body ?? "").trim() || "(no description)";

  return [
    `Repository: ${descriptor.repository}`,
    `Organization: ${organizationOf(descriptor.repository)}`,
    `Pull request: #${descriptor.pullNumber} — ${descriptor.title}`,
    `Change size: ${changedFiles} files, +${additions} -${deletions}`,
    "",
    `Source identity and state:\n${sourceSection(input)}`,
    "",
    `PR description:\n${description}`,
    "",
    `Changed files:\n${changedFileList(input)}`,
    "",
    `People:\n${peopleSection(input)}`,
    "",
    `Review signals:\n${reviewSignalsSection(input)}`,
    "",
    `CHEW review guidance:\n${guidanceSection(input)}`,
    "",
    `Focused-file semantics:\n${semanticsSection(input)}`,
    "",
    `Comments:\n${commentsSection(input)}`,
    "",
    `Focused file: ${input.path}`,
    `Unified diff for ${input.path}:\n${fileDiffText(input.file, rows, input.fileSemantics?.focus)}`,
  ].join("\n");
}

export interface FileChatStreamOptions {
  assistant: FileChatAssistant;
  model?: string;
  context: string;
  source?: FileChatSource;
  mode?: FileChatMode;
  messages: FileChatMessage[];
  signal?: AbortSignal;
  onDelta: (delta: string) => void;
  onStatus?: (label: string) => void;
  onActivity?: (activity: FileChatActivity) => void;
}

const pairingStorageKey = "chew:file-chat-pairing";
let pairingCode: string | undefined;

export function storedFileChatPairing(): string {
  if (pairingCode !== undefined) return pairingCode;
  try { return sessionStorage.getItem(pairingStorageKey) ?? ""; } catch { return ""; }
}

export function persistFileChatPairing(value: string): void {
  const code = value.trim();
  if (code && !parseFileChatPairing(code)) throw new Error("Paste the complete pairing code printed by the bridge.");
  pairingCode = code;
  try {
    if (code) sessionStorage.setItem(pairingStorageKey, code);
    else sessionStorage.removeItem(pairingStorageKey);
  } catch { /* Pairing still works when session storage is unavailable. */ }
}

export function parseFileChatPairing(code: string): { port: number; token: string } | undefined {
  const match = /^(\d{1,5})\.([a-f0-9]{48})$/.exec(code);
  if (!match || Number(match[1]) < 1 || Number(match[1]) > 65535) return undefined;
  return { port: Number(match[1]), token: match[2]! };
}

async function bridgeFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const pairing = parseFileChatPairing(storedFileChatPairing());
  if (!pairing) throw new Error("Start the local bridge, then paste its pairing code into Connect local bridge.");
  const request: RequestInit & { targetAddressSpace: "loopback" } = {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${pairing.token}` },
    credentials: "omit",
    referrerPolicy: "no-referrer",
    targetAddressSpace: "loopback",
  };
  let response: Response;
  try {
    response = await fetch(`http://127.0.0.1:${pairing.port}${path}`, request);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw new Error("Cannot reach the local bridge. Keep it running and allow local network access for CHEW in your browser's site settings. Try current Chrome or Edge if your browser blocks the connection.");
  }
  if (response.status === 401) throw new Error("The pairing code is no longer valid. Paste the code from your running bridge and connect again.");
  return response;
}

/**
 * Stream normalized JSONL events from the local companion. Each vendor CLI has
 * a different event schema; the server keeps that churn out of the browser.
 */
export async function streamFileChat(options: FileChatStreamOptions): Promise<void> {
  const response = await bridgeFetch("/file-chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      assistant: options.assistant,
      model: options.model || undefined,
      context: options.context,
      source: options.source,
      mode: options.mode,
      messages: options.messages,
    }),
    signal: options.signal,
  });

  if (!response.ok || !response.body) {
    let message = "The review assistant could not respond.";
    try {
      const payload = (await response.json()) as { error?: { message?: string } };
      if (payload?.error?.message) message = payload.error.message;
    } catch {
      // Non-JSON error body; keep the default message.
    }
    throw new Error(message);
  }

  if (!response.headers.get("Content-Type")?.toLowerCase().includes("application/x-ndjson")) {
    const legacyReader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    for (;;) {
      const { done, value } = await legacyReader.read();
      if (value) options.onDelta(value);
      if (done) return;
    }
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let pending = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (value) pending += value;
    const lines = pending.split("\n");
    pending = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.trim()) continue;
      const event = parseFileChatEvent(line);
      if (event.type === "delta") options.onDelta(event.delta);
      if (event.type === "status") options.onStatus?.(event.label);
      if (event.type === "activity") options.onActivity?.(event.activity);
      if (event.type === "error") {
        await reader.cancel();
        throw new Error(event.message);
      }
    }
    if (done) break;
  }
  if (pending.trim()) {
    try {
      const event = parseFileChatEvent(pending);
      if (event.type === "delta") options.onDelta(event.delta);
      if (event.type === "status") options.onStatus?.(event.label);
      if (event.type === "activity") options.onActivity?.(event.activity);
      if (event.type === "error") throw new Error(event.message);
    } catch (reason) {
      if (reason instanceof SyntaxError) return;
      throw reason;
    }
  }
}

export async function loadFileChatConnection(signal?: AbortSignal): Promise<{ assistants: FileChatCapability[]; bridgeVersion?: string }> {
  const response = await bridgeFetch("/capabilities", { signal });
  if (!response.ok) throw new Error(`The local chat companion returned HTTP ${response.status}.`);
  const payload = await response.json() as { assistants?: unknown; bridgeVersion?: unknown };
  if (!Array.isArray(payload.assistants)) throw new Error("The local chat companion returned malformed capabilities.");
  const assistants = payload.assistants.filter((capability): capability is FileChatCapability => {
    if (!capability || typeof capability !== "object") return false;
    const id = (capability as { id?: unknown }).id;
    return fileChatAssistants.includes(id as FileChatAssistant);
  });
  return { assistants, bridgeVersion: typeof payload.bridgeVersion === "string" ? payload.bridgeVersion : undefined };
}

export async function loadFileChatCapabilities(signal?: AbortSignal): Promise<FileChatCapability[]> {
  return (await loadFileChatConnection(signal)).assistants;
}

export async function fileChatUpdateAvailable(installedVersion?: string): Promise<boolean> {
  if (installedVersion === "development") return false;
  try {
    const response = await fetch("/downloads/chew-bridge.json", { cache: "no-store", signal: AbortSignal.timeout(3000) });
    if (!response.ok) return false;
    const release = await response.json() as { version?: unknown };
    return typeof release.version === "string" && /^[a-f0-9]{64}$/.test(release.version)
      && release.version !== installedVersion;
  } catch { return false; }
}

export async function loadFileChatModels(
  assistant: FileChatAssistant,
  signal?: AbortSignal,
): Promise<Pick<FileChatCapability, "defaultModel" | "models" | "discovery">> {
  const response = await bridgeFetch(`/capabilities/${assistant}/models`, { signal });
  if (!response.ok) throw new Error(`Model discovery returned HTTP ${response.status}.`);
  const payload = await response.json() as Partial<FileChatCapability>;
  if (!payload.defaultModel || !Array.isArray(payload.models) || !payload.discovery) {
    throw new Error("The local companion returned malformed model capabilities.");
  }
  return {
    defaultModel: payload.defaultModel,
    models: payload.models,
    discovery: payload.discovery,
  };
}
